export interface OperatorI {
  id: string;
  emailAddress?: string;
  operatorUserName?: string;
  firstName?: string;
  lastName?: string;
  // Added for the Operators admin list/edit UI (#1868) — already returned by
  // manage-operators-api (findAll/findOne/update) but previously untyped here.
  phoneNumber?: string;
  operatorPriority?: number;
  uid?: string;
  operatorUserId?: string;
  operatorVerifiedPinId?: string;
  operatorProfileId?: string;
  createdAt?: string;
  updatedAt?: string;
}

// =============================================================================
// Operator Portal v2 — session-console exchange shapes (#1110)
// These mirror the PR1 backend endpoints under /operator. Keep them in sync with
// the controller/service DTOs. In-store-only state (SessionConsoleStateI,
// WizardStateI) lives local to the domain lib, NOT here.
// =============================================================================

/** Today's single truck assignment for the signed-in operator (GET /operator/today). */
export interface TodayAssignmentI {
  eventId: string;
  eventName: string;
  startTime: string;
  endTime: string;
  attendeeCount: number;
  truckId: string;
  truckLabel: string;
  clubName: string;
  /** Mirrors the backend LIABILITY_WAIVER_ENABLED flag (#1828). Off by default. */
  liabilityWaiverEnabled: boolean;
  /** #2080 — true when the signed-in operator created this event themselves
   * (via the quick-add create-event flow) rather than having it assigned to
   * them by an admin. Drives "created by you" vs "pre-assigned by your Tenant Admin"
   * copy on the Today's Assignment card. */
  createdByOperator: boolean;
}

/**
 * Lifecycle status for an attendee in the session console.
 *
 * The backend currently returns `upcoming | tested | no-show | canceled`. The
 * FE adds `arrived` (after check-in) and `in-progress` (after a test starts)
 * locally so the Ready-to-Test list can filter on `arrived`. `next` is the
 * computed-up-next marker for the first queued attendee.
 */
export type AttendeeStatus = 'upcoming' | 'next' | 'arrived' | 'tested' | 'no-show' | 'in-progress' | 'canceled';

/** A single attendee row in the operator session roster. */
export interface OperatorAttendeeRowI {
  bookingId: string;
  customerId: string;
  firstName: string;
  lastInitial: string;
  email: string;
  slotStartTime: string | null;
  isPaid: boolean;
  isWalkUp: boolean;
  status: AttendeeStatus;
  /** Present once the attendee has been checked in (#888 — keyed by checkInId). */
  checkInId?: string;
  /** Present once a result has been submitted. */
  fatPercent?: number;
}

/**
 * A calibration / operator self test open on the session (#2399).
 *
 * Deliberately NOT an `OperatorAttendeeRowI`: an attendee row is booking-shaped
 * (bookingId, customerId, email, isPaid) and an equipment test has none of
 * those — no customer, no booking, no payment, no report.
 */
export interface OperatorEquipmentTestRowI {
  checkInId: string;
  label: string;
  startedAt: string;
  /** open = no readings yet · in-progress = readings captured, not computed ·
   *  computed = a result exists. There is no delivered state — the backend
   *  refuses to submit an equipment test. */
  status: 'open' | 'in-progress' | 'computed';
}

/** Full session detail for an open testing session (GET /operator/sessions/:id). */
export interface OperatorSessionDetailI {
  sessionId: string;
  eventId: string;
  truckId: string;
  truckLabel: string;
  clubName: string;
  operatorName: string;
  startedAt: string;
  attendees: OperatorAttendeeRowI[];
  /** #2399 — calibration runs on this session, listed apart from attendees. */
  equipmentTests: OperatorEquipmentTestRowI[];
  /** Mirrors the backend LIABILITY_WAIVER_ENABLED flag (#1828). Off by default. */
  liabilityWaiverEnabled: boolean;
}

// =============================================================================
// Operator schedule / profile shapes (#2399)
// The portal previously had no way to see an event that wasn't dated today, so
// an event created for any other date silently vanished after creation.
// =============================================================================

/** Lifecycle status of a testing event, mirroring the Prisma enum. */
export type OperatorEventStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'CANCELLED' | 'COMPLETED' | 'NOT_SCHEDULED';

/** One row of the operator's own schedule (GET /operator/events). */
export interface OperatorEventSummaryI {
  eventId: string;
  eventName: string;
  /** Event start as a UTC ISO instant; render in Central time. */
  testEventDate: string;
  /** Stored start/end strings — a CT-offset ISO for anything created since
   *  #2088, a bare "HH:mm" for older rows. Consumers must tolerate both. */
  startTime: string;
  endTime: string;
  clubName: string;
  truckId: string;
  truckLabel: string;
  attendeeCount: number;
  status: OperatorEventStatus;
  createdByOperator: boolean;
  isToday: boolean;
}

/** The operator's schedule either side of today (GET /operator/events). */
export interface OperatorScheduleI {
  upcoming: OperatorEventSummaryI[];
  recent: OperatorEventSummaryI[];
}

/**
 * Identity echo for the profile's diagnostics block (GET /operator/me).
 * `operatorRecordFound: false` is the single most common reason the portal
 * renders empty, and used to be completely invisible to the operator.
 */
export interface OperatorMeI {
  email: string;
  operatorRecordFound: boolean;
  operatorId: string | null;
  firstName: string | null;
  lastName: string | null;
  linkedUserFound: boolean;
  tenantId: string | null;
}

/** One past session on the operator's history (GET /operator/sessions/history). */
export interface OperatorSessionHistoryItemI {
  sessionId: string;
  eventId: string | null;
  eventName: string;
  clubName: string;
  truckLabel: string;
  startedAt: string;
  status: string;
  clientsTested: number;
  noShows: number;
  equipmentTests: number;
}

/** Latest truck readiness checklist (GET /operator/equipment). */
export interface OperatorChecklistI {
  completedAt: string | null;
  scaleZeroed: boolean;
  truckLeveled: boolean;
  tankLidsSet: boolean;
  waterHeaterOn: boolean;
  generatorOilOk: boolean;
  propaneAboveHalf: boolean;
  chlorinePpm: number | null;
  tankTempF: number | null;
  ambientTempF: number | null;
}

/** Latest truck service record (GET /operator/equipment). */
export interface OperatorMaintenanceI {
  serviceDate: string;
  serviceType: string;
  mileage: number | null;
  vendor: string | null;
}

/** Truck + readiness/service state for the profile (GET /operator/equipment). */
export interface OperatorEquipmentI {
  truckId: string | null;
  truckLabel: string;
  truckCode: string | null;
  lastChecklist: OperatorChecklistI | null;
  lastMaintenance: OperatorMaintenanceI | null;
}

/** Body for POST /operator/sessions/:id/equipment-test. */
export interface StartEquipmentTestPayloadI {
  label?: string;
  sex: 'MALE' | 'FEMALE' | 'UNKNOWN';
  gender?: string;
  birthDate: string;
}

/** Server-authoritative computed body-composition result (POST .../compute). */
export interface ComputedResultI {
  fatPercent: number;
  leanLbs: number;
  fatLbs: number;
  weight: number;
  rmr: number;
  density: number;
}

/** A single hydrostatic wet-weight reading captured during the test. */
export interface HydroReadingI {
  wetKg: number;
}
